import React, { memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Input, InputNumber, theme } from "antd";
import PropTypes from "prop-types";
import { useSortable } from "@dnd-kit/sortable";
import { HolderOutlined } from "@ant-design/icons";

const EditableContext = React.createContext(null);

const RowContext = React.createContext({});

export const DragHandle = () => {
	const { setActivatorNodeRef, listeners } = useContext(RowContext);
	return (
		<Button
			type="text"
			size="small"
			icon={<HolderOutlined />}
			style={{
				cursor: "move",
			}}
			ref={setActivatorNodeRef}
			{...listeners}
		/>
	);
};

export const EditableCell = memo(({	
	editable,
	children,
	dataIndex,
	record,
	handleSave,
	inputType = "text",
	prefix = "",
	disabled = false,
	cleanValue = null,
	rowClassName = () => {},
	...restProps
}) => {
	const { token : antToken } = theme.useToken();

	const [editing, setEditing] = useState(false);
	const inputRef = useRef(null);
	const form = useContext(EditableContext);
	
	const statusToSet = rowClassName(record, dataIndex) !== "" ? "warning" : "";
	const colorToSet = statusToSet === "warning" ? antToken.colorYellow : "#d9d9d9";

	useEffect(() => {
		if (disabled) return;
		if (editing) {
			inputRef.current?.focus();
		}
	}, [editing]);

	const toggleEdit = () => {
		if (disabled) return;
		setEditing(!editing);
		form.setFieldsValue({
			[dataIndex]: record[dataIndex],
		});
	};

	const save = async () => {
		try {
			const values = await form.validateFields();
			let newValue = values[dataIndex];

			if (cleanValue) {
				newValue = cleanValue(newValue);
			}

			toggleEdit();
			handleSave({
				...record,
				[dataIndex]: newValue,
			});
		} catch (errInfo) {
			console.log("Save failed:", errInfo);
		}
	};

	const getInput = () => {
		switch (inputType) {
		case "number":
			return (
				<InputNumber 
					ref={inputRef} 
					min={0.01} 
					precision={2}
					step={0.01}
					onPressEnter={save} 
					onBlur={save}					
					status={statusToSet}
					placeholder={statusToSet}
					formatter={(value) => `${prefix} ${value}`}
					parser={(value) => value.replace(prefix, "").trim()}
					disabled={disabled}
					controls={false}				
				/>
			);
		default:
			return (
				<Input
					ref={inputRef}
					onPressEnter={save}
					onBlur={save}
					status={statusToSet}
					placeholder={statusToSet}
					disabled={disabled}
				/>
			);
		}
	};

	const divStyle = {
		height: "19px",
		paddingInlineEnd: 24,		
		border: `1px solid ${colorToSet}`,
		borderRadius: "11px",
		padding: "6px 11px",
		cursor: disabled ? "not-allowed" : "pointer",
		opacity: disabled ? 0.5 : 1,
		transition: "border-color 0.3s, box-shadow 0.3s",
		boxShadow: editing ? "0 0 0 2px rgba(24, 144, 255, 0.2)" : "none",		
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: "100%",
	};
		
	let childNode = children;
	if (editable) {
		childNode = editing ? (
			<Form.Item style={{ margin: 0 }} name={dataIndex}>
				{getInput()}
			</Form.Item>
		) : (
			<div className="editable-cell-value-wrap" style={divStyle} onClick={toggleEdit}>
				{prefix} {children}
			</div>
		);
	}

	return <td {...restProps}>{childNode}</td>;
});

export const EditableRowForTable = memo(({ children, ...props }) => {
	const { listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
		id: props["data-row-key"]
	});

	const [form] = Form.useForm();
	
	const style = {
		...props.style,
		transform: transform ? `translateY(${transform.y}px)` : undefined,
		transition,
		cursor: "default",
		...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
	};

	const rowContextValue = useMemo(
		() => ({
			setActivatorNodeRef,
			listeners,
		}),
		[setActivatorNodeRef, listeners]
	);

	return (
		<Form form={form} component={false}>			
			<EditableContext.Provider value={form}>
				<RowContext.Provider value={rowContextValue}>
					<tr
						{...props}
						ref={setNodeRef} // Necesario para el arrastre
						style={style}
						onMouseDown={(e) => {
							const targetTag = e.target.tagName;
							if (["INPUT", "TEXTAREA", "SELECT", "BUTTON", "LABEL"].includes(targetTag)) {
								e.stopPropagation(); // Evita conflicto entre edición y drag & drop
							}
						}}
					>
						{children}
					</tr>
				</RowContext.Provider>
			</EditableContext.Provider>
		</Form>
	);
});

EditableCell.displayName = "EditableCell";

EditableCell.propTypes = {	
	editable: PropTypes.bool,
	children: PropTypes.node,
	dataIndex: PropTypes.number,
	record: PropTypes.object,
	handleSave: PropTypes.func,
	inputType: PropTypes.string,
	prefix: PropTypes.string,
	disabled: PropTypes.bool,	
	cleanValue: PropTypes.func,
	rowClassName: PropTypes.func,
};

EditableRowForTable.displayName = "EditableRowForTable";

EditableRowForTable.propTypes = {
	id: PropTypes.string,
	"data-row-key": PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	style: PropTypes.object,
	className: PropTypes.string,
	children: PropTypes.node,
	onClick: PropTypes.func,
	attributes: PropTypes.object,
	listeners: PropTypes.object,
	setNodeRef: PropTypes.func,
	transform: PropTypes.object,
	transition: PropTypes.string,
	isDragging: PropTypes.bool,
};

